/**
 * Duration-proportional call journey visual (spec §6). Inline SVG so it
 * renders under SSR (no canvas dependency like the ECharts-based charts in
 * `common/charts/`); segment widths are proportional to `durationSeconds`
 * with a floor so zero-duration hops (carrier, SBC) still read as a visible
 * step. Colour comes only from `useChartColors()` and Fluent `tokens.*`
 * (CSS variable references, not literals) -- see the no-colour-literal
 * assertion in `JourneyFlow.test.tsx`.
 */
import { useId, useMemo } from 'react';
import { Text, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import type { Journey, JourneyNode, JourneyNodeKind, JourneyQuality } from '../../utils/journeyTiers';
import { useChartColors } from '../common/charts/chartTheme';
import { formatPhoneNumber } from '../../utils/phone';
import { strings } from '../../strings/en';

const MIN_SEG = 48; // px floor so zero-duration hops still read
const NODE_R = 14;
const H = 96;
// Heuristic character width (px) at the node-label font size, and the label
// wrap/measurement budget (spec send-back G2: six nodes crammed left because
// segment width only tracked duration, ignoring how wide the two labels at
// its ends actually are).
const LABEL_CHAR_PX = 8;
export const MAX_LABEL_WIDTH = 140;
const MAX_LABEL_LINES = 2;
const LABEL_LINE_HEIGHT = 13;

export interface JourneyLayout {
  nodes: { id: string; x: number; node: JourneyNode }[];
  segments: { fromX: number; toX: number; durationSeconds: number | null; outcome: string | null }[];
}

// Only these node kinds carry a raw phone number alongside `label`/`name`
// (journeyTiers.ts) -- every other kind (AA/CQ/main line/carrier/etc.) has
// an already-composed `label` that must not be replaced by `name` alone.
const PHONE_BEARING_KINDS = new Set<JourneyNodeKind>(['external', 'person']);

/**
 * What a node shows: `primary` is the name (if any -- otherwise the
 * formatted number, otherwise the existing composed label) and `secondary`
 * is the formatted number shown underneath when a name is also present
 * (spec send-back G3: a caller-ID name must not make the number disappear).
 * Exported for tests.
 */
export function nodeDisplay(node: JourneyNode): { primary: string; secondary: string | null } {
  if (!PHONE_BEARING_KINDS.has(node.kind)) {
    return { primary: node.label, secondary: null };
  }
  if (node.name) {
    return { primary: node.name, secondary: node.number ? formatPhoneNumber(node.number) : null };
  }
  if (node.number) {
    return { primary: formatPhoneNumber(node.number), secondary: null };
  }
  return { primary: node.label, secondary: null };
}

/** Heuristic pixel width for a one-line label, capped at the wrap budget since a wrapped label never needs more than that on layout. */
function labelWidthPx(label: string): number {
  return Math.min(MAX_LABEL_WIDTH, LABEL_CHAR_PX * label.length);
}

/**
 * Greedily wraps `label` onto at most `MAX_LABEL_LINES` lines within
 * `maxWidth`, breaking on spaces; a single word wider than the budget is
 * hard-split (the SVG-text equivalent of CSS `overflow-wrap`). Exported for
 * tests.
 */
export function wrapNodeLabel(
  label: string,
  maxWidth: number = MAX_LABEL_WIDTH,
  charPx: number = LABEL_CHAR_PX,
): string[] {
  const maxChars = Math.max(1, Math.floor(maxWidth / charPx));
  const words = label.split(' ').filter(Boolean);
  if (words.length === 0) return [label];

  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (word.length > maxChars) {
      if (current) {
        lines.push(current);
        current = '';
      }
      let remaining = word;
      while (remaining.length > maxChars) {
        lines.push(remaining.slice(0, maxChars));
        remaining = remaining.slice(maxChars);
      }
      current = remaining;
      continue;
    }
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);

  if (lines.length > MAX_LABEL_LINES) {
    const head = lines.slice(0, MAX_LABEL_LINES - 1);
    const tail = lines.slice(MAX_LABEL_LINES - 1).join(' ');
    return [...head, tail];
  }
  return lines;
}

/**
 * Pure layout: each segment gets a minimum width sufficient for the longer
 * of its two end-node labels (not just a flat floor), then the remaining
 * width is distributed proportionally to duration -- so several near-zero
 * legs next to long labels no longer crowd every label into an overlapping
 * pile on the left (spec send-back G2). Exported for tests.
 */
export function layoutJourney(j: Journey, width: number): JourneyLayout {
  const pad = NODE_R + 8;
  const nodeById = new Map(j.nodes.map((n) => [n.id, n]));
  const segMinWidths = j.segments.map((s) => {
    const fromNode = nodeById.get(s.fromId);
    const toNode = nodeById.get(s.toId);
    const fromWidth = fromNode ? labelWidthPx(nodeDisplay(fromNode).primary) : 0;
    const toWidth = toNode ? labelWidthPx(nodeDisplay(toNode).primary) : 0;
    return Math.max(MIN_SEG, fromWidth, toWidth);
  });
  const floorTotal = segMinWidths.reduce((s, w) => s + w, 0);
  const usable = Math.max(width - pad * 2, floorTotal);
  const total = j.segments.reduce((s, x) => s + (x.durationSeconds ?? 0), 0);
  const flex = Math.max(usable - floorTotal, 0);
  let x = pad;
  const xs: Record<string, number> = {};
  if (j.nodes[0]) xs[j.nodes[0].id] = x;
  const segments = j.segments.map((s, i) => {
    const share = total > 0 ? (s.durationSeconds ?? 0) / total : 1 / j.segments.length;
    const w = segMinWidths[i] + flex * share;
    const fromX = x;
    x += w;
    xs[s.toId] = x;
    return { fromX, toX: x, durationSeconds: s.durationSeconds, outcome: s.outcome };
  });
  return { nodes: j.nodes.map((n) => ({ id: n.id, x: xs[n.id] ?? pad, node: n })), segments };
}

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS },
  svg: { width: '100%', height: `${H}px`, overflow: 'visible' },
  narrative: { color: tokens.colorNeutralForeground2 },
  // Griffel has no '@keyframes <name>' rule key: keyframes are an object given
  // directly to animationName, and Griffel generates and injects the name.
  path: {
    strokeDasharray: 1,
    strokeDashoffset: 0,
    animationName: { from: { strokeDashoffset: 1 }, to: { strokeDashoffset: 0 } },
    animationDuration: '900ms',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'both',
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
  },
});

/** `m:ss` under each segment (spec §6); no label at all when duration is unknown, never "0:00". */
function fmt(s: number | null): string {
  if (s == null) return '';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return strings.interaction.segmentDuration(m, r);
}

export function JourneyFlow({
  journey,
  narrative,
  width = 800,
}: {
  journey: Journey;
  narrative?: string;
  width?: number;
}) {
  const styles = useStyles();
  const colors = useChartColors();
  const id = useId();
  const layout = useMemo(() => layoutJourney(journey, width), [journey, width]);
  const qualityColor = (q: JourneyQuality | null) =>
    q === 'healthy' ? colors.positive : q === 'degraded' ? colors.warning : q === 'poor' ? colors.negative : colors.muted;
  const y = H / 2;

  if (journey.nodes.length === 0) {
    return <Text className={styles.narrative}>{narrative ?? strings.interaction.journeyUnavailable}</Text>;
  }
  return (
    <div className={styles.wrap}>
      <svg className={styles.svg} viewBox={`0 0 ${width} ${H}`} role="img" aria-labelledby={`${id}-title`}>
        <title id={`${id}-title`}>{narrative}</title>
        {layout.segments.map((s, i) => (
          <g key={i}>
            <line
              x1={s.fromX}
              y1={y}
              x2={s.toX}
              y2={y}
              pathLength={1}
              className={styles.path}
              stroke={s.outcome === 'no_answer' || s.outcome === 'failed' ? colors.negative : colors.emphasis}
              strokeWidth={3}
              style={{ animationDelay: `${i * 120}ms` }}
            />
            {/* Never a fabricated "0:00" -- no label at all under an
                unknown or sub-second leg (spec send-back G2). */}
            {s.durationSeconds != null && s.durationSeconds >= 1 && (
              <text x={(s.fromX + s.toX) / 2} y={y + 28} textAnchor="middle" fontSize={11} fill={tokens.colorNeutralForeground3}>
                {fmt(s.durationSeconds)}
              </text>
            )}
          </g>
        ))}
        {layout.nodes.map(({ x, node }) => {
          const display = nodeDisplay(node);
          const primaryLines = wrapNodeLabel(display.primary);
          // The formatted number (when a name is also present) renders as
          // its own smaller, muted line closest to the node -- a "Caption1"
          // treatment done in raw SVG text (spec send-back G3).
          const labelLines = display.secondary ? [...primaryLines, display.secondary] : primaryLines;
          const baseY = y - NODE_R - 8;
          const topY = baseY - (labelLines.length - 1) * LABEL_LINE_HEIGHT;
          const tooltipContent = display.secondary ? `${display.primary} (${display.secondary})` : display.primary;
          return (
            <Tooltip key={node.id} content={tooltipContent} relationship="label">
              <g data-node={node.kind} data-quality={node.quality ?? undefined}>
                <circle cx={x} cy={y} r={NODE_R} fill={tokens.colorNeutralBackground1} stroke={colors.emphasis} strokeWidth={2} />
                {node.quality && <circle cx={x + NODE_R - 3} cy={y - NODE_R + 3} r={5} fill={qualityColor(node.quality)} />}
                <text x={x} y={topY} textAnchor="middle" fontSize={12} fill={tokens.colorNeutralForeground1}>
                  {labelLines.map((line, i) => {
                    const isSecondary = display.secondary != null && i === labelLines.length - 1;
                    return (
                      <tspan
                        key={i}
                        x={x}
                        dy={i === 0 ? 0 : LABEL_LINE_HEIGHT}
                        fontSize={isSecondary ? 10 : 12}
                        fill={isSecondary ? tokens.colorNeutralForeground3 : tokens.colorNeutralForeground1}
                      >
                        {line}
                      </tspan>
                    );
                  })}
                </text>
              </g>
            </Tooltip>
          );
        })}
      </svg>
      {narrative && (
        <Text size={200} className={styles.narrative}>
          {narrative}
        </Text>
      )}
    </div>
  );
}
